// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// Level & Subquests für die Fortschritt-Seite (Thors Feedback Runde 8):
// Meilensteine werden Level, jedes Level hat kleine Aufgaben („Subquests").
// Alles rechnet auf Bestwerten — nichts wird je wieder aberkannt
// (Gentler-Streak-Gesetz, Skill §6): die „beste Wochen-Serie" bleibt stehen,
// auch wenn eine Pause dazwischen liegt.
import type { WorkoutEntry } from "./types"
import { uniqueDates, totalVolume } from "./stats"

export interface QuestStats {
  einheiten: number
  saetze: number
  volumen: number
  uebungen: number
  besteWochenSerie: number
}

export interface Quest {
  id: string
  titel: string
  ziel: number
  einheit: "" | "kg"
  wert: (s: QuestStats) => number
}

export interface Level {
  stufe: number
  name: string
  quests: Quest[]
}

/** Beste je erreichte Serie von Kalenderwochen mit mindestens einer Einheit. */
export function besteWochenSerie(dates: string[]): number {
  if (!dates.length) return 0
  const montage = [...new Set(dates.map((d) => {
    const t = new Date(d + "T12:00")
    t.setDate(t.getDate() - ((t.getDay() + 6) % 7)) // zurück auf Montag
    return t.toISOString().slice(0, 10)
  }))].sort()
  let beste = 1
  let lauf = 1
  for (let i = 1; i < montage.length; i++) {
    lauf = (Date.parse(montage[i]) - Date.parse(montage[i - 1])) === 7 * 86400000 ? lauf + 1 : 1
    beste = Math.max(beste, lauf)
  }
  return beste
}

export function questStats(entries: WorkoutEntry[]): QuestStats {
  const dates = uniqueDates(entries)
  return {
    einheiten: dates.length,
    saetze: entries.reduce((s, e) => s + (e.sets ?? []).length, 0),
    volumen: Math.round(totalVolume(entries)),
    uebungen: new Set(entries.map((e) => e.exercise)).size,
    besteWochenSerie: besteWochenSerie(dates),
  }
}

function q(id: string, titel: string, ziel: number, wert: (s: QuestStats) => number, einheit: "" | "kg" = ""): Quest {
  return { id, titel, ziel, wert, einheit }
}

// Level-Namen = die bewährten Meilenstein-Texte (1/5/10/25/50 Einheiten
// bleiben die Leit-Quests, damit die Feier-Momente weiter dazu passen).
export const LEVELS: Level[] = [
  { stufe: 1, name: "Der Anfang ist gemacht", quests: [
    q("l1-einheit", "Erste Einheit eintragen", 1, (s) => s.einheiten),
    q("l1-saetze", "10 Sätze schaffen", 10, (s) => s.saetze),
    q("l1-uebungen", "3 verschiedene Übungen ausprobieren", 3, (s) => s.uebungen),
  ] },
  { stufe: 2, name: "Die Routine wächst", quests: [
    q("l2-einheiten", "5 Einheiten sammeln", 5, (s) => s.einheiten),
    q("l2-serie", "2 Wochen am Stück trainieren", 2, (s) => s.besteWochenSerie),
    q("l2-uebungen", "10 verschiedene Übungen kennen", 10, (s) => s.uebungen),
  ] },
  { stufe: 3, name: "Spürbar stabiler", quests: [
    q("l3-einheiten", "10 Einheiten sammeln", 10, (s) => s.einheiten),
    q("l3-volumen", "10.000 kg bewegen", 10000, (s) => s.volumen, "kg"),
    q("l3-serie", "3 Wochen am Stück trainieren", 3, (s) => s.besteWochenSerie),
  ] },
  { stufe: 4, name: "Echte Trainingsgewohnheit", quests: [
    q("l4-einheiten", "25 Einheiten sammeln", 25, (s) => s.einheiten),
    q("l4-volumen", "50.000 kg bewegen", 50000, (s) => s.volumen, "kg"),
    q("l4-uebungen", "20 verschiedene Übungen kennen", 20, (s) => s.uebungen),
  ] },
  { stufe: 5, name: "Stark, stabil, beständig", quests: [
    q("l5-einheiten", "50 Einheiten sammeln", 50, (s) => s.einheiten),
    q("l5-volumen", "150.000 kg bewegen", 150000, (s) => s.volumen, "kg"),
    q("l5-serie", "6 Wochen am Stück trainieren", 6, (s) => s.besteWochenSerie),
  ] },
]

export function questFertig(quest: Quest, s: QuestStats): boolean {
  return quest.wert(s) >= quest.ziel
}

export function levelFertig(level: Level, s: QuestStats): boolean {
  return level.quests.every((qq) => questFertig(qq, s))
}

/** Aktuelles Level = erstes nicht abgeschlossenes; nach dem letzten bleibt das letzte. */
export function aktuellesLevel(s: QuestStats): Level {
  return LEVELS.find((l) => !levelFertig(l, s)) ?? LEVELS[LEVELS.length - 1]
}
